import { PageLayout } from "@/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import AttributesTable from "@/domains/admin/components/organisms/AttributesTable";
import CategoriesTable from "@/domains/admin/components/organisms/CategoriesTable";
import CategoriesTranslationsTable from "@/domains/admin/components/organisms/CategoriesTranslationsTable";
import DesignDisciplinesTable from "@/domains/admin/components/organisms/DesignDisciplinesTable";
import DisciplinesTranslationsTable from "@/domains/admin/components/organisms/DisciplinesTranslationsTable";
import DesignStagesTranslationsTable from "@/domains/admin/components/organisms/DesignStagesTranslationsTable";
import PipelinesTable from "@/domains/admin/components/organisms/PipelinesTable";
import FileTypeMappingsTable from "@/domains/admin/components/organisms/FileTypeMappingsTable";
import FilterHierarchyTable from "@/domains/admin/components/organisms/FilterHierarchyTable";
import PromptsManagement from "@/domains/admin/components/organisms/PromptsManagement";
import ClassHierarchyTable from "@/domains/admin/components/organisms/ClassHierarchyTable";
import RolesTable from "@/domains/admin/components/organisms/RolesTable";
import TemplateLinksTable from "@/domains/admin/components/organisms/TemplateLinksTable";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";

export default function DataManagement() {
  const { t } = useLanguage();

  return (
    <PageLayout headerButtons={<AdminHeader />}>
      <div className="mb-6">
        <PageTitle>Ontology Management</PageTitle>
        <PageSubtitle>
          Manage building data attributes, document categories, and AI prompts
        </PageSubtitle>
      </div>

      <Tabs defaultValue="attributes" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="class-hierarchy">Class Hierarchy</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="pipelines">{t.common.pipelines}</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="attributes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Building Data Attributes</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage the 179 building data attributes organized into 4 categories (Space, System,
                Component, Data)
              </p>
            </CardHeader>
            <CardContent>
              <AttributesTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Document Categories</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage document categories and their types for classification
              </p>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="classification">
                <TabsList className="mb-4">
                  <TabsTrigger value="classification">Classification Pipeline</TabsTrigger>
                  <TabsTrigger value="filtering">Filtering</TabsTrigger>
                  <TabsTrigger value="translations">Translations</TabsTrigger>
                </TabsList>
                <TabsContent value="classification">
                  <CategoriesTable />
                </TabsContent>
                <TabsContent value="filtering">
                  <FilterHierarchyTable />
                </TabsContent>
                <TabsContent value="translations">
                  <CategoriesTranslationsTable />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>File Type Category Mappings</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure automatic category assignment based on file extensions. Files matching
                these mappings can bypass LLM classification entirely or have categories added
                alongside LLM results.
              </p>
            </CardHeader>
            <CardContent>
              <FileTypeMappingsTable />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Design Disciplines</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage the 27 Estonian design disciplines for document classification
              </p>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="management">
                <TabsList className="mb-4">
                  <TabsTrigger value="management">Classification</TabsTrigger>
                  <TabsTrigger value="translations">Translations</TabsTrigger>
                </TabsList>
                <TabsContent value="management">
                  <DesignDisciplinesTable />
                </TabsContent>
                <TabsContent value="translations">
                  <DisciplinesTranslationsTable />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Design Stages</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage the 5 design stages for document classification
              </p>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="translations">
                <TabsList className="mb-4">
                  <TabsTrigger value="translations">Translations</TabsTrigger>
                </TabsList>
                <TabsContent value="translations">
                  <DesignStagesTranslationsTable />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Templates Linking</CardTitle>
              <p className="text-sm text-muted-foreground">
                Tie each &quot;New document&quot; template to the classification its documents are
                born with. The category (and optionally the design stage) is applied automatically,
                with no AI call — a template document&apos;s classification is known the moment the
                user picks the template. Disciplines stay LLM-classified, optionally fed the
                form&apos;s own answers.
              </p>
            </CardHeader>
            <CardContent>
              <TemplateLinksTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="class-hierarchy" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Ontology Class Hierarchy</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage class categories (Space, System, Component, Data) and their sub-classes.
                Attributes are linked to sub-classes for ontology grouping and extraction batching.
              </p>
            </CardHeader>
            <CardContent>
              <ClassHierarchyTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="prompts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Prompts</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage AI prompt templates and rules for document processing pipelines
              </p>
            </CardHeader>
            <CardContent>
              <PromptsManagement />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pipelines" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Stages</CardTitle>
              <p className="text-sm text-muted-foreground">
                View the 7 pipeline processing stages for document analysis
              </p>
            </CardHeader>
            <CardContent>
              <PipelinesTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Roles &amp; Permissions</CardTitle>
              <p className="text-sm text-muted-foreground">
                Each role bundles a set of permissions. Click the permission count in any row to add
                or remove permissions, or create a new role for a custom set.
              </p>
            </CardHeader>
            <CardContent>
              <RolesTable />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
}
